import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { TrophyIcon, AwardIcon, CheckCircleIcon, ClockIcon, FlameIcon, StarIcon } from 'lucide-react';
export const GamificationHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [xp, setXp] = useState(450);
  const [level, setLevel] = useState(3);
  const [streak, setStreak] = useState(4);
  const [dailyChallengeCompleted, setDailyChallengeCompleted] = useState(false);
  const badges = [{
    id: 'self-explorer',
    name: 'Self Explorer',
    description: 'Complete the MBTI assessment',
    icon: '🧠',
    earned: true,
    date: '2 days ago'
  }, {
    id: 'cosmic-navigator',
    name: 'Cosmic Navigator',
    description: 'Complete your astrological chart analysis',
    icon: '✨',
    earned: true,
    date: '2 days ago'
  }, {
    id: 'skill-master',
    name: 'Skill Master',
    description: 'Complete 5 skill development modules',
    icon: '🏆',
    earned: false,
    progress: 2,
    total: 5
  }, {
    id: 'networking-pro',
    name: 'Networking Pro',
    description: 'Connect with 3 industry professionals',
    icon: '🤝',
    earned: false,
    progress: 1,
    total: 3
  }, {
    id: 'consistent-explorer',
    name: 'Consistent Explorer',
    description: 'Maintain a 7-day streak',
    icon: '🔥',
    earned: false,
    progress: streak,
    total: 7
  }];
  const leaderboardData = [{
    id: 1,
    name: 'AstroMaster',
    xp: 3250,
    streak: 12,
    avatar: '👨‍🚀'
  }, {
    id: 2,
    name: 'StarGazer',
    xp: 2980,
    streak: 8,
    avatar: '👩‍🔬'
  }, {
    id: 3,
    name: 'CosmicExplorer',
    xp: 2740,
    streak: 10,
    avatar: '🧑‍🚀'
  }, {
    id: 4,
    name: 'You',
    xp: xp,
    streak: streak,
    avatar: '😎',
    isUser: true
  }, {
    id: 5,
    name: 'NebulaNomad',
    xp: 340,
    streak: 2,
    avatar: '👨‍💻'
  }];
  const completeDailyChallenge = () => {
    setDailyChallengeCompleted(true);
    setXp(xp + 50);
    setStreak(streak + 1);
  };
  const renderProgressBar = (current: number, max: number) => <div className="w-full bg-[#1C2541] rounded-full h-2 mb-1" data-id="element-1024">
      <div className="h-2 rounded-full bg-[#7D56F4]" style={{
      width: `${current / max * 100}%`
    }} data-id="element-1025"></div>
    </div>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="max-w-4xl mx-auto p-4" data-id="element-1026">
      <div className="mb-6" data-id="element-1027">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-1028">
          Gamification Hub
        </h1>
        <p className="text-[#B8C0E0]" data-id="element-1029">
          Track your progress, earn badges, and compete with others
        </p>
      </div>
      {/* Profile summary card */}
      <Card className="mb-6" data-id="element-1030">
        <div className="flex flex-col md:flex-row items-center" data-id="element-1031">
          <div className="w-20 h-20 bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] rounded-full flex items-center justify-center text-3xl mb-4 md:mb-0 md:mr-6" data-id="element-1032">
            😎
          </div>
          <div className="flex-1 text-center md:text-left" data-id="element-1033">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3" data-id="element-1034">
              <h2 className="text-xl font-bold" data-id="element-1035">Your Cosmic Journey</h2>
              <div className="flex items-center mt-2 md:mt-0" data-id="element-1036">
                <Badge color="purple" className="mr-2" data-id="element-1037">
                  <StarIcon size={14} className="mr-1" data-id="element-1038" />
                  {xp} XP
                </Badge>
                <Badge color="gold" data-id="element-1039">
                  <FlameIcon size={14} className="mr-1" data-id="element-1040" />
                  {streak} Day Streak
                </Badge>
              </div>
            </div>
            <div className="mb-4" data-id="element-1041">
              <div className="flex justify-between text-sm mb-1" data-id="element-1042">
                <span data-id="element-1043">Level {level}</span>
                <span data-id="element-1044">Level {level + 1}</span>
              </div>
              {renderProgressBar(xp % 500, 500)}
              <div className="text-xs text-[#B8C0E0] text-center" data-id="element-1045">
                {500 - xp % 500} XP until next level
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-1046">
              {badges.filter(badge => badge.earned).map(badge => <div key={badge.id} className="bg-[#1C2541] rounded-full py-1 px-3 flex items-center" data-id="element-1047">
                    <span className="mr-1" data-id="element-1048">{badge.icon}</span>
                    <span className="text-xs" data-id="element-1049">{badge.name}</span>
                  </div>)}
            </div>
          </div>
        </div>
      </Card>
      {/* Tabs */}
      <div className="flex border-b border-[#3A506B] mb-6" data-id="element-1050">
        <button onClick={() => setActiveTab('dashboard')} className={`py-2 px-4 font-medium text-sm ${activeTab === 'dashboard' ? 'text-white border-b-2 border-[#7D56F4]' : 'text-[#B8C0E0]'}`} data-id="element-1051">
          Dashboard
        </button>
        <button onClick={() => setActiveTab('badges')} className={`py-2 px-4 font-medium text-sm ${activeTab === 'badges' ? 'text-white border-b-2 border-[#7D56F4]' : 'text-[#B8C0E0]'}`} data-id="element-1052">
          Badges
        </button>
        <button onClick={() => setActiveTab('leaderboard')} className={`py-2 px-4 font-medium text-sm ${activeTab === 'leaderboard' ? 'text-white border-b-2 border-[#7D56F4]' : 'text-[#B8C0E0]'}`} data-id="element-1053">
          Leaderboard
        </button>
      </div>
      {/* Tab content */}
      {activeTab === 'dashboard' && <div className="space-y-6" data-id="element-1054">
          {/* Daily challenge */}
          <Card className={dailyChallengeCompleted ? 'border-[#19D3DA]' : ''} data-id="element-1055">
            <div className="flex justify-between items-start mb-4" data-id="element-1056">
              <h3 className="text-lg font-semibold flex items-center" data-id="element-1057">
                <ClockIcon size={20} className="mr-2 text-[#19D3DA]" data-id="element-1058" />
                Daily Challenge
              </h3>
              <Badge color="purple" data-id="element-1059">+50 XP</Badge>
            </div>
            <div className="mb-6" data-id="element-1060">
              <h4 className="font-medium mb-2" data-id="element-1061">Innovation Reflection</h4>
              <p className="text-[#B8C0E0] text-sm" data-id="element-1062">
                Answer 3 questions about innovation in your field of interest to
                earn XP and maintain your streak.
              </p>
            </div>
            {dailyChallengeCompleted ? <div className="flex items-center text-[#19D3DA]" data-id="element-1063">
                <CheckCircleIcon size={18} className="mr-2" data-id="element-1064" />
                <span data-id="element-1065">Completed! Come back tomorrow for a new challenge.</span>
              </div> : <div className="space-y-4" data-id="element-1066">
                <div data-id="element-1067">
                  <p className="text-sm mb-2" data-id="element-1068">
                    1. What innovation most excites you in your field?
                  </p>
                  <textarea className="w-full bg-[#1C2541] border border-[#3A506B] rounded p-2 text-white" rows={2} data-id="element-1069"></textarea>
                </div>
                <div data-id="element-1070">
                  <p className="text-sm mb-2" data-id="element-1071">
                    2. How might you contribute to innovation in your career?
                  </p>
                  <textarea className="w-full bg-[#1C2541] border border-[#3A506B] rounded p-2 text-white" rows={2} data-id="element-1072"></textarea>
                </div>
                <div data-id="element-1073">
                  <p className="text-sm mb-2" data-id="element-1074">
                    3. What skill would help you be more innovative?
                  </p>
                  <textarea className="w-full bg-[#1C2541] border border-[#3A506B] rounded p-2 text-white" rows={2} data-id="element-1075"></textarea>
                </div>
                <div className="flex justify-end" data-id="element-1076">
                  <button onClick={completeDailyChallenge} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-2 px-6 rounded-full" data-id="element-1077">
                    Submit Challenge
                  </button>
                </div>
              </div>}
          </Card>
          {/* Next milestone */}
          <Card data-id="element-1078">
            <h3 className="text-lg font-semibold mb-4" data-id="element-1079">Next Milestone</h3>
            <div className="flex items-center" data-id="element-1080">
              <div className="w-12 h-12 bg-[#1C2541] rounded-full flex items-center justify-center text-xl mr-4" data-id="element-1081">
                🔥
              </div>
              <div className="flex-1" data-id="element-1082">
                <h4 className="font-medium" data-id="element-1083">7-Day Streak</h4>
                <p className="text-[#B8C0E0] text-sm mb-2" data-id="element-1084">
                  Log in for {7 - streak} more days to earn the Consistent
                  Explorer badge
                </p>
                {renderProgressBar(streak, 7)}
              </div>
            </div>
          </Card>
        </div>}
      {activeTab === 'badges' && <div className="space-y-4" data-id="element-1085">
          <div className="mb-4" data-id="element-1086">
            <h2 className="text-lg font-semibold flex items-center" data-id="element-1087">
              <AwardIcon size={18} className="text-[#FFD700] mr-2" data-id="element-1088" />
              Career Badges
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1089">
            {badges.map((badge, index) => <motion.div key={badge.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: index * 0.1
        }} data-id="element-1090">
                <Card className={`${badge.earned ? '' : 'opacity-70'}`} data-id="element-1091">
                  <div className="flex" data-id="element-1092">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl mr-4 ${badge.earned ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500]' : 'bg-[#1C2541]'}`} data-id="element-1093">
                      {badge.icon}
                    </div>
                    <div className="flex-1" data-id="element-1094">
                      <div className="flex justify-between items-start" data-id="element-1095">
                        <h3 className="font-medium" data-id="element-1096">{badge.name}</h3>
                        {badge.earned && <Badge color="gold" data-id="element-1097">Earned</Badge>}
                      </div>
                      <p className="text-sm text-[#B8C0E0] mb-2" data-id="element-1098">
                        {badge.description}
                      </p>
                      {badge.earned ? <div className="text-xs text-[#B8C0E0]" data-id="element-1099">
                          Earned {badge.date}
                        </div> : <div data-id="element-1100">
                          {renderProgressBar(badge.progress!, badge.total!)}
                          <div className="text-xs text-[#B8C0E0]" data-id="element-1101">
                            Progress: {badge.progress}/{badge.total}
                          </div>
                        </div>}
                    </div>
                  </div>
                </Card>
              </motion.div>)}
          </div>
        </div>}
      {activeTab === 'leaderboard' && <div className="space-y-4" data-id="element-1102">
          <div className="flex justify-between items-center mb-4" data-id="element-1103">
            <h2 className="text-lg font-semibold flex items-center" data-id="element-1104">
              <TrophyIcon size={18} className="text-[#FFD700] mr-2" data-id="element-1105" />
              Top Explorers
            </h2>
            <Badge color="purple" data-id="element-1106">
              <FlameIcon size={14} className="mr-1" data-id="element-1107" />
              Your Streak: {streak} days
            </Badge>
          </div>
          {/* Sort leaderboard by XP */}
          {[...leaderboardData].sort((a, b) => b.xp - a.xp).map((user, index) => <motion.div key={user.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: index * 0.1
      }} data-id="element-1108">
                <Card className={`flex items-center ${user.isUser ? 'border-[#7D56F4]' : ''}`} data-id="element-1109">
                  <div className="w-8 h-8 flex items-center justify-center font-bold" data-id="element-1110">
                    {index + 1}
                  </div>
                  <div className="w-10 h-10 bg-[#1C2541] rounded-full flex items-center justify-center text-xl mr-3" data-id="element-1111">
                    {user.avatar}
                  </div>
                  <div className="flex-1" data-id="element-1112">
                    <div className="flex justify-between" data-id="element-1113">
                      <span className={`font-medium ${user.isUser ? 'text-[#7D56F4]' : ''}`} data-id="element-1114">
                        {user.name} {user.isUser && '(You)'}
                      </span>
                      <span className="text-[#19D3DA] font-bold" data-id="element-1115">
                        {user.xp} XP
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#B8C0E0]" data-id="element-1116">
                      <span data-id="element-1117">Rank #{index + 1}</span>
                      <span className="flex items-center" data-id="element-1118">
                        <FlameIcon size={12} className="text-[#FF8F5E] mr-1" data-id="element-1119" />
                        {user.streak} day streak
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>)}
        </div>}
    </motion.div>;
};